module Main exposing (Model, Msg, main)

import Browser
import Browser.Navigation as Nav
import Html exposing (Html)
import Http
import Json.Decode as Decode
import Orchestra.Api as Api
import Orchestra.Core.Context exposing (Context)
import Orchestra.Core.Session exposing (SessionToken)
import Orchestra.Core.User exposing (Profile)
import Orchestra.Env exposing (Env, IdToken)
import Orchestra.Extra.Cmd as Cmd
import Orchestra.Extra.Tuple as Tuple
import Orchestra.Global exposing (GlobalMsg(..))
import Orchestra.Pages.Admin.Page as Admin
import Orchestra.Pages.Dashboard.Page as Dashboard
import Orchestra.Pages.Error.Page as Error
import Orchestra.Pages.Loading.Page as Loading
import Orchestra.Pages.Login.Page as Login
import Orchestra.Pages.LoginCallback.Page as LoginCallback
import Orchestra.Pages.Musicians.Page as Musicians
import Orchestra.Pages.NotFound.Page as NotFound
import Orchestra.Pages.Plannings.Page as Plannings
import Orchestra.Pages.Plannings.ViewPlanning.Page as ViewPlanning
import Orchestra.Pages.Plannings.ViewPlanning.Schedule as Schedule
import Orchestra.Pages.Redirect.Page as Redirect
import Orchestra.Ports as Ports
import Orchestra.Route as Route exposing (Route, routeToString)
import Url exposing (Url)



-- MAIN


main : Program Decode.Value Model Msg
main =
    Browser.application
        { init = init
        , view = view
        , update = update
        , subscriptions = subscriptions
        , onUrlChange = UrlChanged
        , onUrlRequest = LinkClicked
        }



-- MODEL


type alias Model =
    { navKey : Nav.Key
    , mainModel : MainModel
    }


overrideMainModel : Model -> MainModel -> Model
overrideMainModel model mainModel =
    { model | mainModel = mainModel }


type MainModel
    = Errored String
    | RestoringSession Env Url
    | NotLoggedIn Env Login.Model
    | LoginCallback Env LoginCallback.Model
    | Ready
        { env : Env
        , profile : Profile
        , content : Content
        , toast : Maybe Toast
        }


extractSessionToken : MainModel -> Maybe SessionToken
extractSessionToken mainModel =
    case mainModel of
        Errored _ ->
            Nothing

        RestoringSession { sessionToken } _ ->
            sessionToken

        NotLoggedIn { sessionToken } _ ->
            sessionToken

        LoginCallback { sessionToken } _ ->
            sessionToken

        Ready { env } ->
            env.sessionToken


type Content
    = NotFound
    | Redirect
    | Dashboard Dashboard.Model
    | Musicians Musicians.Model
    | Plannings Plannings.Model
    | Admin Admin.Model


extractContent : MainModel -> Maybe Content
extractContent mainModel =
    case mainModel of
        Ready { content } ->
            Just content

        _ ->
            Nothing


extractMusiciansModel : Content -> Maybe Musicians.Model
extractMusiciansModel content =
    case content of
        Musicians model ->
            Just model

        _ ->
            Nothing


extractPlanningsModel : Content -> Maybe Plannings.Model
extractPlanningsModel content =
    case content of
        Plannings model ->
            Just model

        _ ->
            Nothing


type alias Toast =
    { message : String }


init : Decode.Value -> Url.Url -> Nav.Key -> ( Model, Cmd Msg )
init envJs url navKey =
    case Decode.decodeValue Ports.envDecoder envJs of
        Err err ->
            Decode.errorToString err
                |> Errored
                |> Tuple.withSnd Cmd.none
                |> Tuple.mapFirst (\mainModel -> { navKey = navKey, mainModel = mainModel })

        Ok env ->
            case Route.fromUrl url of
                Just Route.LoginCallback ->
                    LoginCallback.init
                        |> Tuple.mapBoth (LoginCallback env) (Cmd.map GotLoginCallbackMsg)
                        |> Tuple.mapFirst (\mainModel -> { navKey = navKey, mainModel = mainModel })

                _ ->
                    case env.sessionToken of
                        Nothing ->
                            Login.init
                                |> Tuple.mapBoth (NotLoggedIn env) (Cmd.map GotLoginMsg)
                                |> Tuple.mapFirst (\mainModel -> { navKey = navKey, mainModel = mainModel })

                        Just sessionToken ->
                            RestoringSession env url
                                |> Tuple.withSnd Cmd.none
                                |> Cmd.withCmd (Api.getProfile sessionToken ProfileReceived)
                                |> Tuple.mapFirst (\mainModel -> { navKey = navKey, mainModel = mainModel })



-- UPDATE


type Msg
    = -- Navigation
      LinkClicked Browser.UrlRequest
    | UrlChanged Url.Url
      -- Main state's messages
    | GotLoginMsg Login.Msg
    | GotLoginCallbackMsg LoginCallback.Msg
      -- Pages
    | GotDashboardMsg Dashboard.Msg
    | GotMusiciansMsg Musicians.Msg
    | GotPlanningsMsg Plannings.Msg
    | GotAdminMsg Admin.Msg
      -- Global commands
    | ProfileReceived (Result Http.Error Profile)
    | LoginStarted (Result Http.Error Api.LoginStart)
    | LoginReceived (Maybe IdToken)
    | LoginValidationReceived (Result Http.Error Api.LoginValidated)
    | GotGlobalMsg GlobalMsg


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case model.mainModel of
        Errored _ ->
            ( model, Cmd.none )

        RestoringSession env url ->
            case msg of
                ProfileReceived profileOrFailure ->
                    case profileOrFailure of
                        Err _ ->
                            Login.init
                                |> Tuple.mapBoth (NotLoggedIn env) (Cmd.map GotLoginMsg)
                                |> Tuple.mapFirst (overrideMainModel model)

                        Ok profile ->
                            Ready
                                { env = env
                                , profile = profile
                                , content = Redirect
                                , toast = Nothing
                                }
                                |> overrideMainModel model
                                |> Tuple.withSnd Cmd.none
                                |> changeRouteTo (Route.fromUrl url)

                _ ->
                    ( model, Cmd.none )

        NotLoggedIn env notLoggedIn ->
            case msg of
                GotLoginMsg subMsg ->
                    let
                        ( subModel, subCmd, subGlobalCmd ) =
                            Login.update subMsg notLoggedIn
                    in
                    ( NotLoggedIn env subModel, Cmd.batch [ Cmd.map GotLoginMsg subCmd, Cmd.map GotGlobalMsg subGlobalCmd ] )
                        |> Tuple.mapFirst (overrideMainModel model)

                GotGlobalMsg RequestLogin ->
                    ( model, Api.startLogin LoginStarted )

                LoginStarted loginStartedOrFailure ->
                    case loginStartedOrFailure of
                        Err err ->
                            ( NotLoggedIn env <| Login.withErrorMessage (loginHttpErrorToString err) notLoggedIn, Cmd.none )
                                |> Tuple.mapFirst (overrideMainModel model)

                        Ok { nonce } ->
                            let
                                baseUrl : Url
                                baseUrl =
                                    env.baseUrl
                            in
                            ( model
                            , Ports.requestLogin
                                { redirectUri = { baseUrl | path = routeToString Route.LoginCallback } |> Url.toString
                                , nonce = nonce
                                }
                            )

                _ ->
                    ( model, Cmd.none )

        LoginCallback env loginCallback ->
            case msg of
                GotLoginCallbackMsg subMsg ->
                    LoginCallback.update subMsg loginCallback
                        |> Tuple.mapBoth (LoginCallback env) (Cmd.map GotLoginCallbackMsg)
                        |> Tuple.mapFirst (overrideMainModel model)

                LoginReceived maybeIdToken ->
                    case maybeIdToken of
                        Nothing ->
                            Login.init
                                |> Tuple.mapBoth (NotLoggedIn env << Login.withErrorMessage "No id token received") (Cmd.map GotLoginMsg)
                                |> Cmd.withCmd (Nav.pushUrl model.navKey <| Route.routeToString Route.Dashboard)
                                |> Tuple.mapFirst (overrideMainModel model)

                        Just idToken ->
                            ( model
                            , Api.validateLogin LoginValidationReceived { idToken = idToken }
                            )

                LoginValidationReceived loginValidatedOrFailure ->
                    case loginValidatedOrFailure of
                        Err err ->
                            Login.init
                                |> Tuple.mapBoth (NotLoggedIn env << Login.withErrorMessage (loginHttpErrorToString err)) (Cmd.map GotLoginMsg)
                                |> Cmd.withCmd (Nav.pushUrl model.navKey <| Route.routeToString Route.Dashboard)
                                |> Tuple.mapFirst (overrideMainModel model)

                        Ok { profile, sessionToken } ->
                            Dashboard.init sessionToken
                                |> Tuple.mapBoth (\home -> Ready { env = { env | sessionToken = Just sessionToken }, profile = profile, content = Dashboard home, toast = Nothing }) (Cmd.map GotDashboardMsg)
                                |> Cmd.withCmd (Ports.persistSessionToken sessionToken)
                                |> Cmd.withCmd (Nav.pushUrl model.navKey <| Route.routeToString Route.Dashboard)
                                |> Tuple.mapFirst (overrideMainModel model)

                _ ->
                    ( model, Cmd.none )

        Ready ready ->
            case ( msg, ready.content ) of
                ( LinkClicked urlRequest, _ ) ->
                    case urlRequest of
                        Browser.Internal url ->
                            ( model, Nav.pushUrl model.navKey (Url.toString url) )

                        Browser.External href ->
                            ( model, Nav.load href )

                ( UrlChanged url, _ ) ->
                    ( model, Cmd.none )
                        |> changeRouteTo (Route.fromUrl url)

                ( GotGlobalMsg RequestLogout, _ ) ->
                    let
                        env : Env
                        env =
                            ready.env
                    in
                    Login.init
                        |> Tuple.mapBoth (NotLoggedIn { env | sessionToken = Nothing }) (Cmd.map GotLoginMsg)
                        |> Cmd.withCmd Ports.requestLogout
                        |> Cmd.withCmd (Nav.pushUrl model.navKey <| Route.routeToString Route.Dashboard)
                        |> Tuple.mapFirst (overrideMainModel model)

                ( GotGlobalMsg (RequestRouteChange newRoute), _ ) ->
                    ( model, Nav.pushUrl model.navKey <| Route.routeToString newRoute )

                ( GotDashboardMsg subMsg, Dashboard home ) ->
                    Dashboard.update subMsg home
                        |> handleGlobalMsg (updateContentWith Dashboard GotDashboardMsg model)

                ( GotMusiciansMsg subMsg, Musicians musicians ) ->
                    Musicians.update subMsg musicians
                        |> handleGlobalMsg (updateContentWith Musicians GotMusiciansMsg model)

                ( GotPlanningsMsg subMsg, Plannings musicians ) ->
                    Plannings.update subMsg musicians
                        |> handleGlobalMsg (updateContentWith Plannings GotPlanningsMsg model)

                ( GotAdminMsg subMsg, Admin musicians ) ->
                    Admin.update subMsg musicians
                        |> handleGlobalMsg (updateContentWith Admin GotAdminMsg model)

                _ ->
                    ( model, Cmd.none )


changeRouteTo : Maybe Route -> ( Model, Cmd Msg ) -> ( Model, Cmd Msg )
changeRouteTo maybeRoute ( model, _ ) =
    let
        withSessionData : (Model -> Maybe a) -> (a -> ( Model, Cmd Msg )) -> ( Model, Cmd Msg )
        withSessionData fExtract f =
            case fExtract model of
                Nothing ->
                    Errored "Invalid routing request"
                        |> overrideMainModel model
                        |> Tuple.withSnd Cmd.none

                Just a ->
                    f a
    in
    case maybeRoute of
        Nothing ->
            NotFound
                |> Tuple.withSnd Cmd.none
                |> updateContentWith identity identity model

        Just Route.LoginCallback ->
            NotFound
                |> Tuple.withSnd Cmd.none
                |> updateContentWith identity identity model

        Just Route.Dashboard ->
            withSessionData (extractSessionToken << .mainModel) <|
                \sessionToken ->
                    Dashboard.init sessionToken
                        |> updateContentWith Dashboard GotDashboardMsg model

        Just (Route.Musicians subRoute) ->
            withSessionData (extractSessionToken << .mainModel) <|
                \sessionToken ->
                    let
                        goToMusicianSubRoute : ( Musicians.Model, Cmd Musicians.Msg ) -> ( Musicians.Model, Cmd Musicians.Msg )
                        goToMusicianSubRoute ( subModel, subCmd ) =
                            case subRoute of
                                Route.MusiciansHome ->
                                    Musicians.goToHome subModel
                                        |> Cmd.withPrecedingCmd subCmd

                                Route.MusiciansAdd ->
                                    Musicians.goToAddMusician subModel
                                        |> Cmd.withPrecedingCmd subCmd

                                Route.MusicianView musicianId ->
                                    Musicians.goToViewMusician musicianId subModel
                                        |> Cmd.withPrecedingCmd subCmd
                    in
                    model.mainModel
                        |> extractContent
                        |> Maybe.andThen extractMusiciansModel
                        |> Maybe.map (Cmd.addCmd Cmd.none)
                        |> Maybe.withDefault (Musicians.init sessionToken)
                        |> goToMusicianSubRoute
                        |> updateContentWith Musicians GotMusiciansMsg model

        Just (Route.Plannings subRoute) ->
            withSessionData (extractSessionToken << .mainModel) <|
                \sessionToken ->
                    let
                        goToPlanningSubRoute : ( Plannings.Model, Cmd Plannings.Msg ) -> ( Plannings.Model, Cmd Plannings.Msg )
                        goToPlanningSubRoute ( subModel, subCmd ) =
                            case subRoute of
                                Route.PlanningHome ->
                                    Plannings.goToHome subModel
                                        |> Cmd.withPrecedingCmd subCmd

                                Route.PlanningView planningId tabRoute ->
                                    let
                                        tab : ViewPlanning.PlanningTab
                                        tab =
                                            case tabRoute of
                                                Route.SummaryTab ->
                                                    ViewPlanning.SummaryTab

                                                Route.ScheduledMusiciansTab ->
                                                    ViewPlanning.ScheduledMusiciansTab

                                                Route.SeriesTab ->
                                                    ViewPlanning.SeriesTab

                                                Route.ScheduleTab Route.PlanningScheduleHome ->
                                                    ViewPlanning.ScheduleTab Schedule.NoSelection

                                                Route.ScheduleTab (Route.PlanningScheduleSerie serieId) ->
                                                    ViewPlanning.ScheduleTab <| Schedule.SelectedSerie serieId

                                                Route.ScheduleTab (Route.PlanningScheduleMusician musicianId) ->
                                                    ViewPlanning.ScheduleTab <| Schedule.SelectedMusician musicianId

                                                Route.ScheduleTab Route.PlanningScheduleAutomatic ->
                                                    ViewPlanning.ScheduleTab Schedule.AutomaticAssigningSelected
                                    in
                                    Plannings.goToViewPlanning planningId tab subModel
                                        |> Cmd.withPrecedingCmd subCmd
                    in
                    model.mainModel
                        |> extractContent
                        |> Maybe.andThen extractPlanningsModel
                        |> Maybe.map (Cmd.addCmd Cmd.none)
                        |> Maybe.withDefault (Plannings.init sessionToken)
                        |> goToPlanningSubRoute
                        |> updateContentWith Plannings GotPlanningsMsg model

        Just Route.Administration ->
            withSessionData (extractSessionToken << .mainModel) <|
                \sessionToken ->
                    Admin.init sessionToken
                        |> updateContentWith Admin GotAdminMsg model


updateContentWith : (subModel -> Content) -> (subMsg -> Msg) -> Model -> ( subModel, Cmd subMsg ) -> ( Model, Cmd Msg )
updateContentWith toModel toMsg model ( subModel, subCmd ) =
    case model.mainModel of
        Errored _ ->
            ( model, Cmd.none )

        RestoringSession _ _ ->
            ( model, Cmd.none )

        NotLoggedIn _ _ ->
            ( model, Cmd.none )

        LoginCallback _ _ ->
            ( model, Cmd.none )

        Ready ready ->
            ( { model | mainModel = Ready { ready | content = toModel subModel } }
            , Cmd.map toMsg subCmd
            )


handleGlobalMsg : (( subModel, Cmd subMsg ) -> ( Model, Cmd Msg )) -> ( subModel, Cmd subMsg, Cmd GlobalMsg ) -> ( Model, Cmd Msg )
handleGlobalMsg f ( subModel, cmdSubMsg, cmdGlobalMsg ) =
    let
        ( newModel, newCmd ) =
            f ( subModel, cmdSubMsg )
    in
    ( newModel, Cmd.batch [ newCmd, Cmd.map GotGlobalMsg cmdGlobalMsg ] )


loginHttpErrorToString : Http.Error -> String
loginHttpErrorToString error =
    case error of
        Http.BadUrl url ->
            "The login URL " ++ url ++ " was invalid."

        Http.Timeout ->
            "Unable to reach the server, try again later..."

        Http.NetworkError ->
            "Unable to reach the server, check your network connection."

        Http.BadStatus 500 ->
            "The server had a problem, try again later..."

        Http.BadStatus status ->
            "Unknown error (" ++ String.fromInt status ++ "). Please try again later..."

        Http.BadBody errorMessage ->
            errorMessage



-- VIEW


view : Model -> Browser.Document Msg
view model =
    let
        titleSuffix : String
        titleSuffix =
            " | Orchestra"

        toBrowserDocument : (subMsg -> Msg) -> { title : String, content : Html subMsg } -> { title : String, body : List (Html Msg) }
        toBrowserDocument f { title, content } =
            { title = title ++ titleSuffix, body = [ Html.map f content ] }
    in
    case model.mainModel of
        Errored err ->
            Error.view err
                |> toBrowserDocument identity

        RestoringSession _ _ ->
            Loading.view
                |> toBrowserDocument identity

        NotLoggedIn _ notLoggedIn ->
            Login.view notLoggedIn
                |> toBrowserDocument GotLoginMsg

        LoginCallback _ loginCallback ->
            LoginCallback.view loginCallback
                |> toBrowserDocument GotLoginCallbackMsg

        Ready { content, profile } ->
            let
                context : Maybe Route -> Context
                context maybeCurrentRoute =
                    { navigation =
                        { links =
                            [ { caption = "Tableau de bord"
                              , isActive = maybeCurrentRoute == Just Route.Dashboard
                              , route = Route.Dashboard
                              }
                            , { caption = "Plannings"
                              , isActive = maybeCurrentRoute == Just (Route.Plannings Route.PlanningHome)
                              , route = Route.Plannings Route.PlanningHome
                              }
                            , { caption = "Musiciens"
                              , isActive = maybeCurrentRoute == Just (Route.Musicians Route.MusiciansHome)
                              , route = Route.Musicians Route.MusiciansHome
                              }
                            , { caption = "Administration"
                              , isActive = maybeCurrentRoute == Just Route.Administration
                              , route = Route.Administration
                              }
                            ]
                        }
                    , profile = profile
                    }
            in
            case content of
                NotFound ->
                    NotFound.view
                        |> toBrowserDocument identity

                Redirect ->
                    Redirect.view
                        |> toBrowserDocument identity

                Dashboard home ->
                    Dashboard.view (context <| Just Route.Dashboard) home
                        |> toBrowserDocument GotDashboardMsg

                Musicians musicians ->
                    Musicians.view (context <| Just <| Route.Musicians Route.MusiciansHome) musicians
                        |> toBrowserDocument GotMusiciansMsg

                Plannings plannings ->
                    Plannings.view (context <| Just <| Route.Plannings Route.PlanningHome) plannings
                        |> toBrowserDocument GotPlanningsMsg

                Admin admin ->
                    Admin.view (context <| Just Route.Administration) admin
                        |> toBrowserDocument GotAdminMsg



-- SUBSCRIPTIONS


subscriptions : Model -> Sub Msg
subscriptions _ =
    Ports.loginReceivedSub LoginReceived
